/**
 * Cattery Settings
 * Manage cattery profile, subscription tier, and preferences
 */

import { useState, useEffect } from 'react';
import { 
  Settings, Building2, User, MapPin, Phone, Mail, Globe, Camera,
  Facebook, Instagram, Shield, CreditCard, Check, AlertCircle,
  Package, Crown, Sparkles, Save, Bell, Eye, EyeOff
} from 'lucide-react';
import { Card, Button, Badge, Modal, Input, Select, Alert, Tabs } from '../shared/UIComponents';
import { formatCurrency, cn } from '../../utils/helpers';
import { useAuth } from '../../hooks/useAuth';
import { SA_CAT_BREEDS, SA_ASSOCIATIONS } from '../../firebase/firestore';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';

// Subscription tiers
const SUBSCRIPTION_TIERS = {
  registry_only: {
    id: 'registry_only',
    name: 'Registry Only',
    description: 'Use registrations and earn from food referrals',
    price: 0,
    priceLabel: 'FREE',
    features: [
      { text: 'SACC/CASA/CFSA registry submissions', included: true },
      { text: '10% registry service fee applies', included: true, note: 'fee' },
      { text: 'Food partner referral program', included: true },
      { text: 'Earn 2% on Hills/Royal Canin referrals', included: true },
      { text: 'List cats on marketplace', included: false },
      { text: 'Sales pipeline & CRM', included: false },
      { text: 'No sales commission', included: true, highlight: true },
    ],
    salesCommission: 0,
    registryFee: 0.10,
    color: 'border-gray-300',
    badge: null
  },
  full_platform: {
    id: 'full_platform',
    name: 'Full Platform',
    description: 'Complete cattery management with marketplace',
    price: 0,
    priceLabel: 'FREE',
    features: [
      { text: 'SACC/CASA/CFSA registry submissions', included: true },
      { text: '10% registry service fee applies', included: true, note: 'fee' },
      { text: 'Food partner referral program', included: true },
      { text: 'Earn 2% on Hills/Royal Canin referrals', included: true },
      { text: 'List cats on marketplace', included: true },
      { text: 'Sales pipeline & CRM', included: true },
      { text: '10% sales commission on completed sales', included: true, note: 'fee' },
    ],
    salesCommission: 0.10,
    registryFee: 0.10,
    color: 'border-feline-500',
    badge: 'Popular'
  }
};

const SA_PROVINCES = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo',
  'Mpumalanga', 'Northern Cape', 'North West', 'Western Cape'
];

export const CatterySettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('subscription');
  const [cattery, setCattery] = useState(null);
  const [showConfirmTier, setShowConfirmTier] = useState(false);
  const [pendingTier, setPendingTier] = useState(null);
  
  const [formData, setFormData] = useState({
    // Basic Info
    catteryName: '',
    catteryPrefix: '',
    ownerName: '',
    establishedYear: '',
    description: '',
    
    // Location
    province: '',
    city: '',
    address: '',
    showAddress: false,
    
    // Contact
    phone: '',
    whatsapp: '',
    email: '',
    website: '',
    facebook: '',
    instagram: '',
    
    // Breeds & Registry
    breeds: [],
    registrations: [],
    
    // Settings
    subscriptionTier: 'full_platform',
    acceptsEnquiries: true,
    showOnMarketplace: true,
    emailNotifications: true,
    smsNotifications: false
  });

  useEffect(() => {
    if (user?.catteryId) loadCattery();
  }, [user?.catteryId]);

  const loadCattery = async () => {
    try {
      setLoading(true);
      const catteryDoc = await getDoc(doc(db, 'catteries', user.catteryId));
      if (catteryDoc.exists()) {
        const data = catteryDoc.data();
        setCattery({ id: catteryDoc.id, ...data });
        setFormData(prev => ({
          ...prev,
          ...data,
          subscriptionTier: data.subscriptionTier || 'full_platform'
        }));
      }
    } catch (error) {
      console.error('Error loading cattery:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateDoc(doc(db, 'catteries', user.catteryId), {
        ...formData,
        updatedAt: serverTimestamp()
      });
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleTierChange = (tierId) => {
    if (tierId === formData.subscriptionTier) return;
    setPendingTier(tierId);
    setShowConfirmTier(true);
  };

  const confirmTierChange = async () => {
    try {
      setSaving(true);
      await updateDoc(doc(db, 'catteries', user.catteryId), {
        subscriptionTier: pendingTier,
        // If switching to registry_only, hide from marketplace
        showOnMarketplace: pendingTier === 'full_platform',
        updatedAt: serverTimestamp()
      });
      setFormData(prev => ({ 
        ...prev, 
        subscriptionTier: pendingTier,
        showOnMarketplace: pendingTier === 'full_platform'
      }));
      setShowConfirmTier(false);
      setPendingTier(null);
    } catch (error) {
      console.error('Error changing tier:', error);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'subscription', label: 'Subscription' },
    { id: 'profile', label: 'Cattery Profile' },
    { id: 'contact', label: 'Contact Info' },
    { id: 'notifications', label: 'Notifications' }
  ];

  const currentTier = SUBSCRIPTION_TIERS[formData.subscriptionTier] || SUBSCRIPTION_TIERS.full_platform;

  if (loading) {
    return <Card><div className="animate-pulse space-y-4 p-8">
      <div className="h-8 bg-gray-200 rounded w-1/3" />
      <div className="h-32 bg-gray-100 rounded" />
    </div></Card>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-gray-900">Settings</h1>
          <p className="text-gray-500">Manage your cattery profile and subscription</p>
        </div>
        <Button onClick={handleSave} loading={saving}>
          <Save className="w-4 h-4" />
          Save Changes
        </Button>
      </div>

      {/* Current Plan Banner */}
      <Card className={cn('border-2', currentTier.color)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={cn(
              'w-12 h-12 rounded-xl flex items-center justify-center',
              formData.subscriptionTier === 'registry_only' ? 'bg-gray-100' : 'bg-feline-100'
            )}>
              {formData.subscriptionTier === 'registry_only' ? (
                <Package className="w-6 h-6 text-gray-600" />
              ) : (
                <Crown className="w-6 h-6 text-feline-600" />
              )}
            </div>
            <div>
              <p className="font-semibold text-gray-900">Current Plan: {currentTier.name}</p>
              <p className="text-sm text-gray-500">{currentTier.description}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">{currentTier.priceLabel}</p>
            {currentTier.salesCommission > 0 && (
              <p className="text-sm text-gray-500">{(currentTier.salesCommission * 100)}% on sales</p>
            )}
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Tab Content */}
      {activeTab === 'subscription' && (
        <div className="space-y-6">
          {/* Tier Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.values(SUBSCRIPTION_TIERS).map((tier) => (
              <Card 
                key={tier.id}
                className={cn(
                  'relative cursor-pointer transition-all border-2',
                  formData.subscriptionTier === tier.id 
                    ? tier.color + ' ring-2 ring-offset-2 ring-feline-500' 
                    : 'border-gray-200 hover:border-gray-300'
                )}
                onClick={() => handleTierChange(tier.id)}
              >
                {tier.badge && (
                  <Badge className="absolute -top-3 left-4 bg-feline-500 text-white">
                    {tier.badge}
                  </Badge>
                )}
                
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{tier.name}</h3>
                    <p className="text-sm text-gray-500">{tier.description}</p>
                  </div>
                  <div className={cn(
                    'w-6 h-6 rounded-full border-2 flex items-center justify-center',
                    formData.subscriptionTier === tier.id 
                      ? 'border-feline-500 bg-feline-500' 
                      : 'border-gray-300'
                  )}>
                    {formData.subscriptionTier === tier.id && (
                      <Check className="w-4 h-4 text-white" />
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <span className="text-3xl font-bold text-gray-900">{tier.priceLabel}</span>
                </div>

                <ul className="space-y-2">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      {feature.included ? (
                        <Check className={cn(
                          'w-5 h-5 flex-shrink-0',
                          feature.highlight ? 'text-paw-500' : 'text-paw-500'
                        )} />
                      ) : (
                        <span className="w-5 h-5 flex-shrink-0 text-gray-300">✕</span>
                      )}
                      <span className={cn(
                        'text-sm',
                        feature.included ? 'text-gray-700' : 'text-gray-400',
                        feature.highlight && 'font-medium text-paw-700'
                      )}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>

          {/* Fee Explanation */}
          <Alert type="info" title="How Fees Work">
            <div className="space-y-2 text-sm">
              <p><strong>Registry Service Fee (10%):</strong> Applied when we submit registrations to SACC/CASA/CFSA on your behalf. You pay the registry fee + our 10% service fee.</p>
              <p><strong>Sales Commission (10%):</strong> Only applies on Full Platform when you complete a sale through our system. Registry Only users don't pay this.</p>
              <p><strong>Food Referrals:</strong> You EARN 2% when buyers purchase Hills or Royal Canin through your referral links. This is income, not a fee!</p>
            </div>
          </Alert>
        </div>
      )}

      {activeTab === 'profile' && (
        <Card>
          <h3 className="font-semibold text-gray-900 mb-4">Cattery Profile</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Cattery Name"
              value={formData.catteryName}
              onChange={(e) => updateField('catteryName', e.target.value)}
            />
            <Input
              label="Cattery Prefix"
              value={formData.catteryPrefix}
              onChange={(e) => updateField('catteryPrefix', e.target.value)}
              maxLength={5}
            />
            <Input
              label="Owner Name"
              value={formData.ownerName}
              onChange={(e) => updateField('ownerName', e.target.value)}
            />
            <Input
              label="Year Established"
              type="number"
              value={formData.establishedYear}
              onChange={(e) => updateField('establishedYear', e.target.value)}
            />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">About</label>
              <textarea
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                rows={4}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-feline-300 focus:border-feline-500 outline-none"
              />
            </div>
            <Select
              label="Province"
              value={formData.province}
              onChange={(e) => updateField('province', e.target.value)}
              options={SA_PROVINCES.map(p => ({ value: p, label: p }))}
            />
            <Input
              label="City/Town"
              value={formData.city}
              onChange={(e) => updateField('city', e.target.value)}
            />
          </div>

          <div className="mt-6 pt-6 border-t">
            <h4 className="font-medium text-gray-900 mb-3">Breeds</h4>
            <div className="flex flex-wrap gap-2">
              {SA_CAT_BREEDS.map(breed => (
                <label key={breed} className={cn(
                  'px-3 py-1.5 rounded-full text-sm cursor-pointer transition-all',
                  formData.breeds?.includes(breed) 
                    ? 'bg-feline-500 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={formData.breeds?.includes(breed)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        updateField('breeds', [...(formData.breeds || []), breed]);
                      } else {
                        updateField('breeds', formData.breeds.filter(b => b !== breed));
                      }
                    }}
                  />
                  {breed}
                </label>
              ))}
            </div>
          </div>

          <div className="mt-6 pt-6 border-t">
            <h4 className="font-medium text-gray-900 mb-3">Registry Memberships</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {SA_ASSOCIATIONS.map(assoc => (
                <label key={assoc.code} className={cn(
                  'flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all',
                  formData.registrations?.includes(assoc.code)
                    ? 'border-feline-500 bg-feline-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}>
                  <input
                    type="checkbox"
                    checked={formData.registrations?.includes(assoc.code)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        updateField('registrations', [...(formData.registrations || []), assoc.code]);
                      } else {
                        updateField('registrations', formData.registrations.filter(r => r !== assoc.code));
                      }
                    }}
                    className="w-5 h-5 text-feline-500 rounded"
                  />
                  <div>
                    <p className="font-medium text-gray-900">{assoc.code}</p>
                    <p className="text-xs text-gray-500">{assoc.name}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'contact' && (
        <Card>
          <h3 className="font-semibold text-gray-900 mb-4">Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Phone"
              value={formData.phone}
              onChange={(e) => updateField('phone', e.target.value)}
              icon={<Phone className="w-5 h-5" />}
            />
            <Input
              label="WhatsApp"
              value={formData.whatsapp}
              onChange={(e) => updateField('whatsapp', e.target.value)}
              icon={<Phone className="w-5 h-5" />}
            />
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => updateField('email', e.target.value)}
              icon={<Mail className="w-5 h-5" />}
            />
            <Input
              label="Website"
              value={formData.website}
              onChange={(e) => updateField('website', e.target.value)}
              icon={<Globe className="w-5 h-5" />}
            />
            <Input
              label="Facebook"
              value={formData.facebook}
              onChange={(e) => updateField('facebook', e.target.value)}
              icon={<Facebook className="w-5 h-5" />}
            />
            <Input
              label="Instagram"
              value={formData.instagram}
              onChange={(e) => updateField('instagram', e.target.value)}
              icon={<Instagram className="w-5 h-5" />}
            />
          </div>

          <div className="mt-6 pt-6 border-t">
            <h4 className="font-medium text-gray-900 mb-3">Visibility</h4>
            <div className="space-y-3">
              <label className="flex items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer">
                <div className="flex items-center gap-3">
                  <Eye className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">Show on Marketplace</p>
                    <p className="text-sm text-gray-500">Your cattery and cats visible to buyers</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={formData.showOnMarketplace}
                  onChange={(e) => updateField('showOnMarketplace', e.target.checked)}
                  disabled={formData.subscriptionTier === 'registry_only'}
                  className="w-5 h-5 text-feline-500 rounded"
                />
              </label>
              {formData.subscriptionTier === 'registry_only' && (
                <p className="text-sm text-amber-600 ml-8">
                  ⚠️ Marketplace listing requires Full Platform subscription
                </p>
              )}
              
              <label className="flex items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">Accept Enquiries</p>
                    <p className="text-sm text-gray-500">Buyers can send you messages</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={formData.acceptsEnquiries}
                  onChange={(e) => updateField('acceptsEnquiries', e.target.checked)}
                  className="w-5 h-5 text-feline-500 rounded"
                />
              </label>
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'notifications' && (
        <Card>
          <h3 className="font-semibold text-gray-900 mb-4">Notification Preferences</h3>
          <div className="space-y-3">
            <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">Email Notifications</p>
                  <p className="text-sm text-gray-500">Receive enquiries and updates via email</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={formData.emailNotifications}
                onChange={(e) => updateField('emailNotifications', e.target.checked)}
                className="w-5 h-5 text-feline-500 rounded"
              />
            </label>
            
            <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">SMS Notifications</p>
                  <p className="text-sm text-gray-500">Get SMS alerts for new enquiries</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={formData.smsNotifications}
                onChange={(e) => updateField('smsNotifications', e.target.checked)}
                className="w-5 h-5 text-feline-500 rounded"
              />
            </label>
          </div>
        </Card>
      )}

      {/* Confirm Tier Change Modal */}
      <Modal
        isOpen={showConfirmTier}
        onClose={() => { setShowConfirmTier(false); setPendingTier(null); }}
        title="Change Subscription"
      >
        {pendingTier && (
          <div className="space-y-4">
            <p className="text-gray-600">
              Are you sure you want to change to <strong>{SUBSCRIPTION_TIERS[pendingTier].name}</strong>?
            </p>
            
            {pendingTier === 'registry_only' && (
              <Alert type="warning">
                <p className="text-sm">
                  Switching to Registry Only will:
                </p>
                <ul className="text-sm mt-2 space-y-1">
                  <li>• Remove your cats from the marketplace</li>
                  <li>• Disable the sales pipeline feature</li>
                  <li>• Remove the 10% sales commission</li>
                </ul>
                <p className="text-sm mt-2">
                  You'll still have access to registrations and food referrals.
                </p>
              </Alert>
            )}

            {pendingTier === 'full_platform' && (
              <Alert type="info">
                <p className="text-sm">
                  Switching to Full Platform will:
                </p>
                <ul className="text-sm mt-2 space-y-1">
                  <li>• Enable marketplace listings</li>
                  <li>• Enable sales pipeline & CRM</li>
                  <li>• 10% commission applies on completed sales</li>
                </ul>
              </Alert>
            )}

            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => { setShowConfirmTier(false); setPendingTier(null); }}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1"
                onClick={confirmTierChange}
                loading={saving}
              >
                Confirm Change
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// Need to import MessageSquare
import { MessageSquare } from 'lucide-react';

export default CatterySettings;
